import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { DesktopNav } from "../components/BottomNav";
import { AppFooter } from "../components/Shell";
import { RaceCard } from "./Races";
import { useAuth } from "../context/AuthContext";
import { useT, LangToggle } from "../i18n";

const HERO = "https://images.unsplash.com/photo-1519505907962-0a6cb0167c73?w=1600&q=80&fit=crop";
const BENEFITS = [["b1t", "b1d"], ["b2t", "b2d"], ["b3t", "b3d"]];
const WRAP = "mx-auto max-w-[1150px] px-5 lg:px-8";

export default function Landing() {
  const { user, profile } = useAuth();
  const { t } = useT();
  const [races, setRaces] = useState([]);
  const [menu, setMenu] = useState(false);
  useEffect(() => {
    api.get("/races/overview").then((r) => setRaces(r.data.filter((x) => !["past", "archived"].includes(x.status)).slice(0, 6))).catch(() => {});
  }, []);
  const ctaTo = user ? (profile ? "/recherche" : "/onboarding") : "/inscription";

  return (
    <div className="min-h-screen bg-rox-black" data-testid="landing-page">
      <header className={`${WRAP} flex h-16 items-center justify-between`}>
        <div className="flex items-center gap-8">
          <Logo className="text-2xl" />
          {user ? <DesktopNav /> : (
            <nav className="hidden items-center gap-1 lg:flex">
              <Link to="/courses" data-testid="landing-nav-races" className="rounded-[3px] px-3 py-2 font-display text-sm uppercase tracking-wider text-rox-off/80 hover:text-rox-off">{t("nav_races")}</Link>
              <a href="#comment" className="rounded-[3px] px-3 py-2 font-display text-sm uppercase tracking-wider text-rox-off/80 hover:text-rox-off">{t("menu_how")}</a>
            </nav>
          )}
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <LangToggle className="hidden sm:flex" />
          <Link to={user ? "/recherche" : "/connexion"} data-testid="landing-login-link" className="text-xs font-semibold uppercase tracking-[0.18em] text-rox-off transition-colors hover:text-rox-orange">
            {user ? t("landing_my_search") : t("landing_login")}
          </Link>
          <button onClick={() => setMenu(!menu)} aria-label="Menu" data-testid="landing-menu" className="text-rox-off lg:hidden">{menu ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
      </header>
      {menu && (
        <nav className={`${WRAP} pb-4 animate-rise-in lg:hidden`} data-testid="landing-menu-panel">
          <div className="panel flex flex-col divide-y divide-rox-line">
            <div className="flex items-center justify-between px-4 py-3 sm:hidden"><span className="text-xs font-semibold uppercase tracking-[0.18em] text-rox-muted">{t("language")}</span><LangToggle /></div>
            {(user ? [["/recherche", t("landing_my_search")], ["/courses", t("nav_races")], ["/profil", t("nav_profile")]] : [["/inscription", t("menu_signup")], ["/connexion", t("menu_login")], ["/courses", t("nav_races")], ["#comment", t("menu_how")]]).map(([to, l]) => (
              <Link key={to} to={to} onClick={() => setMenu(false)} className="px-4 py-3 font-display text-base uppercase tracking-wide hover:text-rox-orange">{l}</Link>
            ))}
          </div>
        </nav>
      )}

      <section className="relative isolate overflow-hidden grain">
        <img src={HERO} alt={t("hero_alt")} className="absolute inset-0 -z-10 h-full w-full object-cover object-center opacity-60 saturate-[0.35] contrast-125" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-rox-black via-rox-black/55 to-rox-black/10" />
        <div className={`${WRAP} grid gap-10 pb-14 pt-28 md:grid-cols-2 md:items-end md:pb-24 md:pt-44`}>
          <div className="stagger">
            <p className="eyebrow text-rox-orange">{t("landing_eyebrow")}</p>
            <h1 className="h-display mt-3 text-5xl sm:text-6xl lg:text-7xl">
              {t("landing_h1a")}
              <br />
              <span className="text-rox-orange">{t("landing_h1b")}</span>
            </h1>
            <p className="mt-6 max-w-sm text-base text-rox-off/90 md:text-lg">{t("landing_sub")}</p>
            <Link to={ctaTo} data-testid="landing-cta" className="btn-primary mt-8 max-w-sm">
              {t("landing_cta")} <ArrowRight size={20} />
            </Link>
            <p className="mt-6 text-sm text-rox-off/60">{t("landing_tagline")}</p>
          </div>
        </div>
      </section>

      <section className={`${WRAP} pb-14 pt-10`} id="courses">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="h-display text-3xl sm:text-4xl">
              {t("landing_h2a")}
              <br />
              <span className="text-rox-orange">{t("landing_h2b")}</span>
            </h2>
            <p className="mt-4 max-w-md text-base text-rox-off/80">{t("landing_h2sub")}</p>
          </div>
          <Link to="/courses" data-testid="landing-races-all" className="inline-flex items-center gap-2 font-display text-sm uppercase tracking-wider text-rox-orange hover:text-rox-off">{t("landing_races_all")} <ArrowRight size={16} /></Link>
        </div>
        <div className="mt-8 grid gap-3 stagger sm:grid-cols-2 lg:grid-cols-3" data-testid="landing-races">
          {races.map((r) => <RaceCard key={r.id} race={r} compact />)}
        </div>
      </section>

      <section className="border-t border-rox-line" id="comment">
        <div className={`${WRAP} grid gap-8 py-14 md:grid-cols-3`}>
          {BENEFITS.map(([tk, dk], i) => (
            <div key={tk} className="border-l-2 border-rox-orange pl-4">
              <p className="eyebrow">0{i + 1}</p>
              <h3 className="h-display mt-1 text-3xl">{t(tk)}</h3>
              <p className="mt-3 text-sm text-rox-off/75">{t(dk)}</p>
            </div>
          ))}
        </div>
        <div className={`${WRAP} pb-16`}>
          <Link to={ctaTo} data-testid="landing-cta-secondary" className="btn-primary max-w-sm">
            {t("landing_cta2")} <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-rox-line">
        <div className={`${WRAP} flex flex-col items-start gap-4 py-12 sm:flex-row sm:items-center sm:justify-between`}>
          <p className="h-display text-3xl">
            {t("landing_footer_a")} <span className="text-rox-orange">{t("landing_footer_b")}</span>
          </p>
          <Logo className="text-xl" />
        </div>
        <div className={WRAP}><AppFooter className="px-0 lg:px-0" withNav={false} /></div>
      </footer>
    </div>
  );
}
